import {
  IContainerControl,
  UIControl,
  SizeConstraint,
  MeasureParams,
  DrawParams,
  PdfSize,
  PdfRect,
  ArrangedChild,
} from '../../types';
import { registry } from '../registry';
import { getExplicitSize } from '../primitives';
import { PdfPen, PdfBrush, PdfColor, ETextAlign, PdfUnit, PdfPoint } from '../../../../pdf/pdf';

const DEFAULT_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
const GAP = 3;

export const StackPanelControl: IContainerControl = {
  type: 'StackPanel',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = control.children || [];
    const orientation = control.attrs.Orientation === 'Horizontal' ? 'Horizontal' : 'Vertical';
    const isHorizontal = orientation === 'Horizontal';

    let totalWidth = 0;
    let totalHeight = 0;

    for (const child of children) {
      const childConstraint = isHorizontal
        ? { width: Number.MAX_VALUE, height: constraint.height }
        : { width: constraint.width, height: Number.MAX_VALUE };
      const childSize = registry.measure(child, childConstraint, params);

      if (isHorizontal) {
        totalWidth += childSize.width + GAP;
        totalHeight = Math.max(totalHeight, childSize.height);
      } else {
        totalWidth = Math.max(totalWidth, childSize.width);
        totalHeight += childSize.height + GAP;
      }
    }

    if (isHorizontal && children.length > 0) totalWidth -= GAP;
    if (!isHorizontal && children.length > 0) totalHeight -= GAP;

    return new PdfSize(explicit.width || totalWidth, explicit.height || totalHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = control.children || [];
    const orientation = control.attrs.Orientation === 'Horizontal' ? 'Horizontal' : 'Vertical';
    const isHorizontal = orientation === 'Horizontal';

    const results: ArrangedChild[] = [];
    let currentX = rect.x;
    let currentY = rect.y;

    for (const child of children) {
      const childConstraint = isHorizontal
        ? { width: Number.MAX_VALUE, height: rect.height }
        : { width: rect.width, height: Number.MAX_VALUE };
      const childSize = registry.measure(child, childConstraint, params);

      let childRect: PdfRect;
      if (isHorizontal) {
        // Children stretch to panel height, use measured width (WPF VerticalAlignment=Stretch default)
        childRect = new PdfRect(currentX, currentY, childSize.width, rect.height);
        currentX += childSize.width + GAP;
      } else {
        // Children stretch to panel width, use measured height (WPF HorizontalAlignment=Stretch default)
        childRect = new PdfRect(currentX, currentY, rect.width, childSize.height);
        currentY += childSize.height + GAP;
      }
      results.push({ child, rect: childRect });
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(StackPanelControl);

export const WrapPanelControl: IContainerControl = {
  type: 'WrapPanel',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = control.children || [];
    const maxWidth = explicit.width || constraint.width || 200;

    let currentX = 0;
    let currentY = 0;
    let lineHeight = 0;
    let itemsInLine = 0;

    for (const child of children) {
      const childSize = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: constraint.height },
        params
      );

      if (currentX + childSize.width > maxWidth && currentX > 0) {
        currentX = 0;
        currentY += lineHeight + GAP;
        lineHeight = 0;
        itemsInLine = 0;
      }

      currentX += childSize.width + GAP;
      lineHeight = Math.max(lineHeight, childSize.height);
      itemsInLine++;
    }

    currentY += lineHeight;

    return new PdfSize(maxWidth, explicit.height || currentY);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = control.children || [];
    const results: ArrangedChild[] = [];

    let currentX = rect.x;
    let currentY = rect.y;
    let lineHeight = 0;
    let itemsInLine = 0;

    for (const child of children) {
      const childSize = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: rect.height },
        params
      );

      if (currentX + childSize.width > rect.x + rect.width && currentX > rect.x) {
        currentX = rect.x;
        currentY += lineHeight + GAP;
        lineHeight = 0;
        itemsInLine = 0;
      }

      const childRect = new PdfRect(currentX, currentY, childSize.width, childSize.height);
      results.push({ child, rect: childRect });

      currentX += childSize.width + GAP;
      lineHeight = Math.max(lineHeight, childSize.height);
      itemsInLine++;
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(WrapPanelControl);

export const DockPanelControl: IContainerControl = {
  type: 'DockPanel',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = control.children || [];
    const lastChildFill = control.attrs.LastChildFill !== 'false';

    let topHeight = 0,
      bottomHeight = 0,
      leftWidth = 0,
      rightWidth = 0;
    const topChildren: UIControl[] = [];
    const bottomChildren: UIControl[] = [];
    const leftChildren: UIControl[] = [];
    const rightChildren: UIControl[] = [];
    const centerChildren: UIControl[] = [];

    for (const child of children) {
      const dock = child.attrs.Dock || 'Top';
      switch (dock) {
        case 'Top':
          topChildren.push(child);
          break;
        case 'Bottom':
          bottomChildren.push(child);
          break;
        case 'Left':
          leftChildren.push(child);
          break;
        case 'Right':
          rightChildren.push(child);
          break;
        default:
          centerChildren.push(child);
      }
    }

    for (const child of topChildren)
      topHeight +=
        registry.measure(child, { width: constraint.width, height: Number.MAX_VALUE }, params)
          .height + GAP;
    for (const child of bottomChildren)
      bottomHeight +=
        registry.measure(child, { width: constraint.width, height: Number.MAX_VALUE }, params)
          .height + GAP;
    for (const child of leftChildren)
      leftWidth = Math.max(
        leftWidth,
        registry.measure(child, { width: Number.MAX_VALUE, height: Number.MAX_VALUE }, params).width
      );
    for (const child of rightChildren)
      rightWidth = Math.max(
        rightWidth,
        registry.measure(child, { width: Number.MAX_VALUE, height: Number.MAX_VALUE }, params).width
      );

    const fillChildren =
      lastChildFill && centerChildren.length === 0
        ? children.filter(
            (c) =>
              !topChildren.includes(c) &&
              !bottomChildren.includes(c) &&
              !leftChildren.includes(c) &&
              !rightChildren.includes(c)
          )
        : centerChildren;

    let centerWidth = 50;
    let centerHeight = 30;
    for (const child of fillChildren) {
      const size = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: Number.MAX_VALUE },
        params
      );
      centerWidth = Math.max(centerWidth, size.width);
      centerHeight = Math.max(centerHeight, size.height);
    }

    const totalWidth = leftWidth + centerWidth + rightWidth + GAP * 2;
    const totalHeight = topHeight + centerHeight + bottomHeight + GAP * 2;

    return new PdfSize(explicit.width || totalWidth, explicit.height || totalHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = control.children || [];
    const lastChildFill = control.attrs.LastChildFill !== 'false';

    const topChildren = children.filter((c) => c.attrs.Dock === 'Top');
    const bottomChildren = children.filter((c) => c.attrs.Dock === 'Bottom');
    const leftChildren = children.filter((c) => c.attrs.Dock === 'Left');
    const rightChildren = children.filter((c) => c.attrs.Dock === 'Right');
    let centerChildren = children.filter(
      (c) => !c.attrs.Dock || !['Top', 'Bottom', 'Left', 'Right'].includes(c.attrs.Dock)
    );

    if (lastChildFill && centerChildren.length === 0) {
      centerChildren = children.filter(
        (c) =>
          !topChildren.includes(c) &&
          !bottomChildren.includes(c) &&
          !leftChildren.includes(c) &&
          !rightChildren.includes(c)
      );
    }

    const results: ArrangedChild[] = [];
    let currentY = rect.y;
    let currentX = rect.x;

    let lWidth = 0,
      rWidth = 0,
      tHeight = 0,
      bHeight = 0;
    for (const child of leftChildren)
      lWidth = Math.max(
        lWidth,
        registry.measure(child, { width: Number.MAX_VALUE, height: rect.height }, params).width
      );
    for (const child of rightChildren)
      rWidth = Math.max(
        rWidth,
        registry.measure(child, { width: Number.MAX_VALUE, height: rect.height }, params).width
      );
    for (const child of topChildren)
      tHeight +=
        registry.measure(child, { width: rect.width, height: Number.MAX_VALUE }, params).height +
        GAP;
    for (const child of bottomChildren)
      bHeight +=
        registry.measure(child, { width: rect.width, height: Number.MAX_VALUE }, params).height +
        GAP;

    for (const child of topChildren) {
      const size = registry.measure(child, { width: rect.width, height: Number.MAX_VALUE }, params);
      const childRect = new PdfRect(
        rect.x,
        currentY,
        rect.width - lWidth - rWidth - GAP * 2,
        size.height
      );
      results.push({ child, rect: childRect });
      currentY += size.height + GAP;
    }

    for (const child of leftChildren) {
      const size = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: rect.height },
        params
      );
      const childRect = new PdfRect(
        currentX,
        currentY,
        size.width,
        rect.height - tHeight - bHeight - GAP * 2
      );
      results.push({ child, rect: childRect });
      currentX += size.width + GAP;
    }

    const centerRect = new PdfRect(
      currentX,
      currentY,
      rect.width - lWidth - rWidth - GAP * 2,
      rect.height - tHeight - bHeight - GAP * 2
    );

    const centerIsSingle = centerChildren.length === 1;
    for (const child of centerChildren) {
      const width = centerIsSingle ? centerRect.width : Number.MAX_VALUE;
      const height = centerIsSingle ? centerRect.height : Number.MAX_VALUE;
      const size = registry.measure(child, { width, height }, params);

      const childRect = new PdfRect(
        centerRect.x,
        centerRect.y,
        centerIsSingle ? centerRect.width : size.width,
        centerIsSingle ? centerRect.height : size.height
      );
      results.push({ child, rect: childRect });
    }

    currentX = centerRect.x + centerRect.width + GAP;

    for (const child of rightChildren) {
      const size = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: rect.height },
        params
      );
      const childRect = new PdfRect(
        currentX,
        currentY,
        size.width,
        rect.height - tHeight - bHeight - GAP * 2
      );
      results.push({ child, rect: childRect });
      currentX += size.width + GAP;
    }

    currentY = rect.y + rect.height - bHeight - GAP;

    for (const child of bottomChildren) {
      const size = registry.measure(
        child,
        { width: rect.width - lWidth - rWidth - GAP * 2, height: Number.MAX_VALUE },
        params
      );
      currentY -= size.height;
      const childRect = new PdfRect(
        rect.x + lWidth,
        currentY,
        rect.width - lWidth - rWidth - GAP * 2,
        size.height
      );
      results.push({ child, rect: childRect });
      currentY -= GAP;
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(DockPanelControl);

const parseGridLength = (str: string): { value: number; type: 'Auto' | 'Star' | 'Pixel' } => {
  str = str.trim();
  if (str === 'Auto') return { value: 0, type: 'Auto' };
  if (str === '*') return { value: 1, type: 'Star' };
  if (str.endsWith('*')) return { value: parseFloat(str.slice(0, -1)) || 1, type: 'Star' };
  return { value: parseFloat(str) || 0, type: 'Pixel' };
};

function calculateGridSizes(
  control: UIControl,
  constraint: SizeConstraint,
  params: MeasureParams,
  isArranging: boolean,
  availableWidth?: number,
  availableHeight?: number
): { colWidths: number[]; rowHeights: number[] } {
  const children = (control.children || []).filter(
    (c) => c.type !== 'RowDefinition' && c.type !== 'ColumnDefinition'
  );

  const rowDefs = (control.children || []).filter((c) => c.type === 'RowDefinition');
  const colDefs = (control.children || []).filter((c) => c.type === 'ColumnDefinition');

  let maxRow = 0,
    maxCol = 0;
  for (const child of children) {
    const row = parseInt(child.attrs['Grid.Row'] || '0', 10);
    const col = parseInt(child.attrs['Grid.Column'] || '0', 10);
    const rowSpan = parseInt(child.attrs['Grid.RowSpan'] || '1', 10);
    const colSpan = parseInt(child.attrs['Grid.ColumnSpan'] || '1', 10);
    maxRow = Math.max(maxRow, row + rowSpan - 1);
    maxCol = Math.max(maxCol, col + colSpan - 1);
  }

  const rowCount = Math.max(maxRow + 1, rowDefs.length, 1);
  const colCount = Math.max(maxCol + 1, colDefs.length, 1);

  const rowHeights = rowDefs.map((r) => parseGridLength(r.attrs.Height || 'Auto'));
  const colWidths = colDefs.map((c) => parseGridLength(c.attrs.Width || 'Auto'));

  while (rowHeights.length < rowCount) rowHeights.push({ value: 20, type: 'Auto' });
  while (colWidths.length < colCount) colWidths.push({ value: 50, type: 'Auto' });

  // Pixel values are in mm (same unit as all other XAML dimensions); Star columns start at 0
  const minColWidths: number[] = colWidths.map((c) =>
    c.type === 'Pixel' ? PdfUnit.FromMillimeters(c.value) : 0
  );
  const minRowHeights: number[] = rowHeights.map((r) =>
    r.type === 'Pixel' ? PdfUnit.FromMillimeters(r.value) : 0
  );

  const constraintW = isArranging ? (availableWidth ?? Number.MAX_VALUE) : constraint.width;
  const constraintH = isArranging ? (availableHeight ?? Number.MAX_VALUE) : constraint.height;

  for (const child of children) {
    const size = registry.measure(
      child,
      { width: Number.MAX_VALUE, height: Number.MAX_VALUE },
      params
    );
    const row = parseInt(child.attrs['Grid.Row'] || '0', 10);
    const col = parseInt(child.attrs['Grid.Column'] || '0', 10);
    const rowSpan = parseInt(child.attrs['Grid.RowSpan'] || '1', 10);
    const colSpan = parseInt(child.attrs['Grid.ColumnSpan'] || '1', 10);

    for (let i = 0; i < colSpan; i++) {
      if (col + i < minColWidths.length && colWidths[col + i].type === 'Auto') {
        minColWidths[col + i] = Math.max(minColWidths[col + i], size.width / colSpan);
      }
    }
    for (let i = 0; i < rowSpan; i++) {
      if (row + i < minRowHeights.length && rowHeights[row + i].type === 'Auto') {
        minRowHeights[row + i] = Math.max(minRowHeights[row + i], size.height / rowSpan);
      }
    }
  }

  const fixedColWidth =
    minColWidths.reduce((sum, w, i) => (colWidths[i].type === 'Star' ? sum : sum + w), 0) +
    GAP * Math.max(0, colCount - 1);
  const fixedRowHeight =
    minRowHeights.reduce((sum, h, i) => (rowHeights[i].type === 'Star' ? sum : sum + h), 0) +
    GAP * Math.max(0, rowCount - 1);

  const starColWidth = colWidths.reduce((sum, c) => sum + (c.type === 'Star' ? c.value : 0), 0);
  const starRowHeight = rowHeights.reduce((sum, r) => sum + (r.type === 'Star' ? r.value : 0), 0);

  const remainingColWidth = Math.max(0, constraintW - fixedColWidth);
  const remainingRowHeight = Math.max(0, constraintH - fixedRowHeight);

  const finalColWidths = minColWidths.map((w, i) => {
    if (colWidths[i].type === 'Star' && starColWidth > 0) {
      return w + (remainingColWidth * colWidths[i].value) / starColWidth;
    }
    return w;
  });

  const finalRowHeights = minRowHeights.map((h, i) => {
    if (rowHeights[i].type === 'Star' && starRowHeight > 0) {
      return h + (remainingRowHeight * rowHeights[i].value) / starRowHeight;
    }
    return h;
  });

  return { colWidths: finalColWidths, rowHeights: finalRowHeights };
}

export const GridControl: IContainerControl = {
  type: 'Grid',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = (control.children || []).filter(
      (c) => c.type !== 'RowDefinition' && c.type !== 'ColumnDefinition'
    );

    if (children.length === 0) {
      return new PdfSize(explicit.width || 50, explicit.height || 30);
    }

    const { colWidths, rowHeights } = calculateGridSizes(control, constraint, params, false);
    const totalWidth =
      colWidths.reduce((a, b) => a + b, 0) + GAP * Math.max(0, colWidths.length - 1);
    const totalHeight =
      rowHeights.reduce((a, b) => a + b, 0) + GAP * Math.max(0, rowHeights.length - 1);

    return new PdfSize(explicit.width || totalWidth, explicit.height || totalHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = (control.children || []).filter(
      (c) => c.type !== 'RowDefinition' && c.type !== 'ColumnDefinition'
    );
    if (children.length === 0) return [];

    const { colWidths, rowHeights } = calculateGridSizes(
      control,
      { width: rect.width, height: rect.height },
      params,
      true,
      rect.width,
      rect.height
    );

    const results: ArrangedChild[] = [];

    for (const child of children) {
      const size = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: Number.MAX_VALUE },
        params
      );
      const row = parseInt(child.attrs['Grid.Row'] || '0', 10);
      const col = parseInt(child.attrs['Grid.Column'] || '0', 10);
      const colSpan = parseInt(child.attrs['Grid.ColumnSpan'] || '1', 10);
      const rowSpan = parseInt(child.attrs['Grid.RowSpan'] || '1', 10);

      let spanWidth = 0;
      for (let i = 0; i < colSpan; i++) {
        if (col + i < colWidths.length) spanWidth += colWidths[col + i];
      }
      spanWidth += GAP * (colSpan - 1);

      let spanHeight = 0;
      for (let i = 0; i < rowSpan; i++) {
        if (row + i < rowHeights.length) spanHeight += rowHeights[row + i];
      }
      spanHeight += GAP * (rowSpan - 1);

      const childX = rect.x + colWidths.slice(0, col).reduce((a, b) => a + b, 0) + GAP * col;
      const childY = rect.y + rowHeights.slice(0, row).reduce((a, b) => a + b, 0) + GAP * row;

      const childRect = new PdfRect(childX, childY, spanWidth, spanHeight);
      results.push({ child, rect: childRect });
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(GridControl);

export const BorderControl: IContainerControl = {
  type: 'Border',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const borderThickness = parseFloat(control.attrs.BorderThickness) || 1;
    const padding = parseFloat(control.attrs.Padding) || 2;
    const innerConstraint = {
      width: Math.max(0, constraint.width - (borderThickness + padding) * 2),
      height: Math.max(0, constraint.height - (borderThickness + padding) * 2),
    };

    const children = control.children || [];
    let childWidth = 50,
      childHeight = 30;
    if (children.length > 0) {
      const size = registry.measure(children[0], innerConstraint, params);
      childWidth = size.width;
      childHeight = size.height;
    }

    const totalWidth = childWidth + (borderThickness + padding) * 2;
    const totalHeight = childHeight + (borderThickness + padding) * 2;

    return new PdfSize(explicit.width || totalWidth, explicit.height || totalHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const borderThickness = parseFloat(control.attrs.BorderThickness) || 1;
    const padding = parseFloat(control.attrs.Padding) || 2;
    const innerRect = new PdfRect(
      rect.x + borderThickness + padding,
      rect.y + borderThickness + padding,
      rect.width - (borderThickness + padding) * 2,
      rect.height - (borderThickness + padding) * 2
    );

    const children = control.children || [];
    if (children.length === 0) return [];

    const childRect = new PdfRect(innerRect.x, innerRect.y, innerRect.width, innerRect.height);

    return [{ child: children[0], rect: childRect }];
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, rect);
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(BorderControl);

export const GroupBoxControl: IContainerControl = {
  type: 'GroupBox',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const headerHeight = 8;
    const borderPadding = 6;

    const children = control.children || [];
    let childWidth = 50,
      childHeight = 30;
    if (children.length > 0) {
      const size = registry.measure(
        children[0],
        { width: constraint.width, height: constraint.height - headerHeight - borderPadding },
        params
      );
      childWidth = size.width;
      childHeight = size.height;
    }

    const totalWidth = childWidth + borderPadding * 2;
    const totalHeight = headerHeight + childHeight + borderPadding;

    return new PdfSize(explicit.width || totalWidth, explicit.height || totalHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const headerHeight = 8;
    const borderPadding = 6;
    const contentRect = new PdfRect(
      rect.x,
      rect.y + headerHeight / 2,
      rect.width,
      rect.height - headerHeight / 2
    );

    const children = control.children || [];
    if (children.length === 0) return [];

    const childRect = new PdfRect(
      contentRect.x + borderPadding,
      contentRect.y + borderPadding,
      contentRect.width - borderPadding * 2,
      contentRect.height - borderPadding * 2
    );

    return [{ child: children[0], rect: childRect }];
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const headerHeight = 8;
    const headerText = control.attrs.Header || '';

    // Draw border rectangle first, then header text on top (masks the border line)
    params.page.drawRectangle(
      DEFAULT_PEN,
      PdfBrush.White,
      new PdfRect(rect.x, rect.y + headerHeight / 2, rect.width, rect.height - headerHeight / 2)
    );

    if (headerText) {
      const textSize = params.page.measureString(headerText, params.font);
      const headerRect = new PdfRect(rect.x + 6, rect.y, textSize.width + 4, headerHeight);
      params.page.drawRectangle(null, PdfBrush.White, headerRect);
      params.page.drawString(
        headerText,
        params.font,
        PdfBrush.Black,
        null,
        headerRect,
        ETextAlign.CenterLeft
      );
    }

    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(GroupBoxControl);

export const TabControlControl: IContainerControl = {
  type: 'TabControl',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const lineH = params.page?.measureString('Ag', params.font).height ?? 10;
    const tabHeight = lineH + GAP * 2;
    const children = control.children || [];

    let maxWidth = 50,
      maxHeight = 30;
    for (const tab of children) {
      if (tab.children && tab.children.length > 0) {
        const size = registry.measure(
          tab.children[0],
          { width: constraint.width, height: constraint.height - tabHeight },
          params
        );
        maxWidth = Math.max(maxWidth, size.width);
        maxHeight = Math.max(maxHeight, size.height);
      }
    }

    return new PdfSize(
      explicit.width || maxWidth + GAP * 2,
      explicit.height || maxHeight + tabHeight + GAP * 2
    );
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const lineH = params.page?.measureString('Ag', params.font).height ?? 10;
    const tabHeight = lineH + GAP * 2;
    const children = control.children || [];
    const results: ArrangedChild[] = [];
    let tabX = rect.x;

    children.forEach((tab, i) => {
      const headerText = tab.attrs.Header || 'Tab';
      const textW =
        params.page?.measureString(headerText, params.font).width ?? headerText.length * 4;
      const tabWidth = textW + GAP * 4;
      const tabRect = new PdfRect(tabX, rect.y, tabWidth, tabHeight);
      const isFirst = i === 0;

      // Active tab (first): no bottom border; inactive: bottom border, light text
      params.page.drawRectangle(null, PdfBrush.White, tabRect);
      if (isFirst) {
        // Top + left + right border, no bottom
        params.page.drawLine(
          DEFAULT_PEN,
          new PdfPoint(tabRect.x, tabRect.y + tabRect.height),
          new PdfPoint(tabRect.x, tabRect.y)
        );
        params.page.drawLine(
          DEFAULT_PEN,
          new PdfPoint(tabRect.x, tabRect.y),
          new PdfPoint(tabRect.x + tabRect.width, tabRect.y)
        );
        params.page.drawLine(
          DEFAULT_PEN,
          new PdfPoint(tabRect.x + tabRect.width, tabRect.y),
          new PdfPoint(tabRect.x + tabRect.width, tabRect.y + tabRect.height)
        );
      } else {
        params.page.drawRectangle(DEFAULT_PEN, null, tabRect);
      }
      params.page.drawString(
        headerText,
        params.font,
        PdfBrush.Black,
        null,
        tabRect,
        ETextAlign.Center
      );
      tabX += tabWidth;
    });

    // Content area: U-shaped border (top has gap under active tab)
    const contentRect = new PdfRect(
      rect.x,
      rect.y + tabHeight,
      rect.width,
      rect.height - tabHeight
    );
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, contentRect);

    const pad = GAP * 2;
    if (children.length > 0 && children[0].children && children[0].children.length > 0) {
      const child = children[0].children[0];
      const childRect = new PdfRect(
        contentRect.x + pad,
        contentRect.y + pad,
        contentRect.width - pad * 2,
        contentRect.height - pad * 2
      );
      results.push({ child, rect: childRect });
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(TabControlControl);
